/**
 * AdminAIHealthBanner — surfaces a heads-up to admin users when AI providers
 * are quota-throttled, before users start hitting "Failed to generate" toasts.
 *
 * Polls /api/admin/ai-health every 2 minutes. Hides itself for non-admins.
 */
import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Warning, Lightning, X } from "@phosphor-icons/react";

export const AdminAIHealthBanner = () => {
  const { user } = useAuth();
  const [health, setHealth] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (user?.role !== "admin") return;
    let alive = true;
    const fetchHealth = async () => {
      try {
        const { data } = await api.get("/admin/ai-health?window_minutes=60");
        if (alive) setHealth(data);
      } catch { /* network error — silent */ }
    };
    fetchHealth();
    const id = setInterval(fetchHealth, 120_000);
    return () => { alive = false; clearInterval(id); };
  }, [user?.role]);

  if (!user || user.role !== "admin" || !health || health.status === "healthy" || dismissed) return null;

  const isCritical = health.status === "critical";
  const palette = isCritical
    ? { bg: "bg-red-500/10", border: "border-red-500/40", text: "text-red-200", icon: "text-red-400" }
    : { bg: "bg-yellow-500/10", border: "border-yellow-500/40", text: "text-yellow-200", icon: "text-yellow-400" };
  const failingModels = (health.models || []).filter(m => m.status !== "healthy");

  return (
    <div className={`${palette.bg} ${palette.border} border-b backdrop-blur-sm px-4 py-2.5`} data-testid="admin-ai-health-banner">
      <div className="max-w-7xl mx-auto flex items-start gap-3">
        {isCritical ? <Warning size={18} weight="fill" className={`${palette.icon} mt-0.5 shrink-0`} /> : <Lightning size={18} weight="fill" className={`${palette.icon} mt-0.5 shrink-0`} />}
        <div className={`flex-1 text-sm ${palette.text}`}>
          <p className="font-medium">{health.message}</p>
          {failingModels.length > 0 && (
            <p className="mt-1 text-xs opacity-80">
              Affected: {failingModels.map(m => `${m.model} (${(m.success_rate * 100).toFixed(0)}% ok, ${m.budget_errors} budget errors)`).join(" · ")}
            </p>
          )}
          <p className="mt-1 text-xs">
            Fix: open Emergent → <b>Profile → Universal Key → Add Balance</b>. Enable auto top-up to prevent recurrence.
          </p>
        </div>
        <button
          onClick={() => setDismissed(true)}
          className={`${palette.text} hover:opacity-70 shrink-0`}
          data-testid="dismiss-ai-health-banner"
          title="Dismiss for this session"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

export default AdminAIHealthBanner;
